import { useState, useRef } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { useAuth } from "@project/contexts/AuthContext";
import {
  mockCandidates,
  mockInterviews,
  mockJobs,
} from "@project/data/mockData";
import {
  Briefcase,
  FileText,
  Calendar,
  CheckCircle2,
  Clock,
  MapPin,
  Building2,
  Upload,
  MessageSquareText,
  Send,
  X,
  Eye,
  File,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { Button } from "@project/components/ui/button";
import { Textarea } from "@project/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";

// Mock HR screening questions
const hrQuestions = [
  {
    id: "q1",
    question: "Why are you interested in this role?",
    required: true,
  },
  {
    id: "q2",
    question: "What is your expected CTC (Current & Expected)?",
    required: true,
  },
  { id: "q3", question: "What is your notice period?", required: true },
  {
    id: "q4",
    question: "Are you comfortable with the job location?",
    required: false,
  },
  {
    id: "q5",
    question: "Do you have any prior experience in a similar domain?",
    required: false,
  },
];

export default function CandidateDashboard() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // HR Questions state
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submittedQuestions, setSubmittedQuestions] = useState(false);

  // Resume upload state
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumePreview, setResumePreview] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);

  const myApplications = mockCandidates.slice(0, 3);
  const myInterviews = mockInterviews.slice(0, 2);

  const stageColors: Record<string, string> = {
    Applied:
      "bg-[hsl(var(--status-posted-bg))] text-[hsl(var(--status-posted))]",
    Screened: "bg-[hsl(var(--info))/0.1] text-[hsl(var(--info))]",
    Shortlisted:
      "bg-[hsl(var(--status-onhold-bg))] text-[hsl(var(--status-onhold))]",
    Interview:
      "bg-[hsl(var(--status-active-bg))] text-[hsl(var(--status-active))]",
    Offered:
      "bg-[hsl(var(--status-active-bg))] text-[hsl(var(--status-active))]",
    Hired: "bg-primary/10 text-primary",
    Rejected: "bg-destructive/10 text-destructive",
  };

  const handleAnswerChange = (qId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmitAnswers = () => {
    const unanswered = hrQuestions.filter(
      q => q.required && !answers[q.id]?.trim()
    );
    if (unanswered.length > 0) {
      toast.error(
        `Please answer all required questions (${unanswered.length} remaining)`
      );
      return;
    }
    setSubmittedQuestions(true);
    toast.success("Your answers have been submitted successfully!");
  };

  const handleResumeSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF and DOCX files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }
    setResumeFile(file);
    toast.success(`Resume "${file.name}" selected`);
  };

  const handleUploadResume = async () => {
    if (!resumeFile) {
      return;
    }
    setUploadingResume(true);
    // Simulate upload
    await new Promise(r => setTimeout(r, 1500));
    setUploadingResume(false);
    toast.success("Resume uploaded successfully!");
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl space-y-6 p-4 pb-24 sm:pb-6 md:p-6">
        <div>
          <h1 className="font-display text-xl font-bold text-foreground md:text-2xl">
            Welcome, {user?.name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track your applications and upcoming interviews
          </p>
        </div>

        {/* Quick Stats */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 gap-3 md:grid-cols-4"
        >
          {[
            {
              label: "Applications",
              value: myApplications.length.toString(),
              icon: FileText,
              accent: "hsl(var(--primary))",
              bg: "hsl(var(--primary) / 0.08)",
            },
            {
              label: "Interviews",
              value: myInterviews.length.toString(),
              icon: Calendar,
              accent: "hsl(38 92% 50%)",
              bg: "hsl(38 92% 50% / 0.08)",
            },
            {
              label: "Shortlisted",
              value: myApplications
                .filter(
                  a => a.stage === "Shortlisted" || a.stage === "Interview"
                )
                .length.toString(),
              icon: CheckCircle2,
              accent: "hsl(160 84% 39%)",
              bg: "hsl(160 84% 39% / 0.08)",
            },
            {
              label: "Offers",
              value: myApplications
                .filter(a => a.stage === "Offered")
                .length.toString(),
              icon: Briefcase,
              accent: "hsl(199 89% 48%)",
              bg: "hsl(199 89% 48% / 0.08)",
            },
          ].map(s => (
            <motion.div
              key={s.label}
              variants={item}
              className="rounded-xl border border-border/60 bg-card p-4"
            >
              <div
                className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ background: s.bg }}
              >
                <s.icon className="h-4 w-4" style={{ color: s.accent }} />
              </div>
              <p className="font-display text-2xl font-bold text-foreground">
                {s.value}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Resume Upload Section */}
        <motion.div
          variants={item}
          initial="hidden"
          animate="show"
          className="overflow-hidden rounded-xl border border-border/60 bg-card"
        >
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <FileText className="h-4 w-4 text-primary" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              My Resume
            </h2>
          </div>
          <div className="p-5">
            {!resumeFile ? (
              <div
                className="cursor-pointer rounded-xl border-2 border-dashed border-border/60 p-8 text-center transition-all hover:border-primary/50 hover:bg-primary/5"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Upload className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  Click to upload your Resume
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  PDF or DOCX • Max 5MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleResumeSelect}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4 rounded-xl border border-border/40 bg-muted/30 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <File className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {resumeFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(resumeFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {resumeFile.type === "application/pdf" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setResumePreview(true)}
                    >
                      <Eye className="mr-1 h-3.5 w-3.5" /> Preview
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-destructive hover:text-destructive"
                    onClick={handleRemoveResume}
                  >
                    <X className="mr-1 h-3.5 w-3.5" /> Remove
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    onClick={handleUploadResume}
                    disabled={uploadingResume}
                  >
                    {uploadingResume ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* HR Screening Questions */}
        <motion.div
          variants={item}
          initial="hidden"
          animate="show"
          className="overflow-hidden rounded-xl border border-border/60 bg-card"
        >
          <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <MessageSquareText className="h-4 w-4 text-primary" />
              <h2 className="font-display text-sm font-semibold text-foreground">
                HR Screening Questions
              </h2>
            </div>
            {submittedQuestions && (
              <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                <CheckCircle2 className="h-3 w-3" /> Submitted
              </span>
            )}
          </div>
          <div className="space-y-4 p-5">
            {submittedQuestions ? (
              <div className="space-y-3">
                {hrQuestions.map((q, idx) => (
                  <div
                    key={q.id}
                    className="rounded-lg border border-border/40 bg-muted/30 p-3"
                  >
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Q{idx + 1}. {q.question}
                    </p>
                    <p className="text-sm text-foreground">
                      {answers[q.id] || "—"}
                    </p>
                  </div>
                ))}
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  Your answers have been submitted to HR.
                </p>
              </div>
            ) : (
              <>
                {hrQuestions.map((q, idx) => (
                  <div key={q.id}>
                    <label className="mb-1.5 flex items-center gap-1 text-sm font-medium text-foreground">
                      Q{idx + 1}. {q.question}
                      {q.required && (
                        <span className="text-destructive">*</span>
                      )}
                    </label>
                    <Textarea
                      placeholder="Type your answer here..."
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      className="min-h-[60px] resize-none rounded-lg text-sm"
                    />
                  </div>
                ))}
                <Button className="w-full gap-2" onClick={handleSubmitAnswers}>
                  <Send className="h-4 w-4" /> Submit Answers
                </Button>
              </>
            )}
          </div>
        </motion.div>

        {/* My Applications */}
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <Briefcase className="h-4 w-4 text-primary" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              My Applications
            </h2>
          </div>
          <div className="divide-y divide-border/40">
            {myApplications.map(app => {
              const job = mockJobs.find(j => j.id === app.jobId);
              return (
                <div
                  key={app.id}
                  className="p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">
                        {app.jobTitle}
                      </p>
                      <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {job?.department || "Engineering"}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job?.location || "Remote"}
                        </span>
                      </div>
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" /> Applied: {app.appliedDate}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${stageColors[app.stage] || "bg-muted text-muted-foreground"}`}
                    >
                      {app.stage}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-1">
                    {[
                      "Applied",
                      "Screened",
                      "Shortlisted",
                      "Interview",
                      "Offered",
                      "Hired",
                    ].map((stage, idx) => {
                      const stageOrder = [
                        "Applied",
                        "Screened",
                        "Shortlisted",
                        "Interview",
                        "Offered",
                        "Hired",
                      ];
                      const currentIdx = stageOrder.indexOf(app.stage);
                      const isActive = idx <= currentIdx;
                      return (
                        <div
                          key={stage}
                          className="flex flex-1 flex-col items-center gap-1"
                        >
                          <div
                            className={`h-1.5 w-full rounded-full ${isActive ? "bg-primary" : "bg-muted"}`}
                          />
                          <span className="hidden text-[9px] text-muted-foreground md:block">
                            {stage}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Interviews */}
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <Calendar className="h-4 w-4 text-warning" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              Upcoming Interviews
            </h2>
          </div>
          <div className="divide-y divide-border/40">
            {myInterviews.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No upcoming interviews
              </div>
            ) : (
              myInterviews.map(interview => (
                <div
                  key={interview.id}
                  className="flex items-center gap-4 p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Calendar className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {interview.jobTitle}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {interview.round} • with {interview.interviewerName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-foreground">
                      {interview.time}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {interview.date}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <Upload className="h-4 w-4 text-primary" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              My Documents
            </h2>
          </div>
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted/50">
              <Upload className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              Upload your documents (Aadhar, Certificates, etc.)
            </p>
            <button className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
              Upload Documents
            </button>
          </div>
        </div>
      </div>

      {/* Resume Preview Dialog */}
      <Dialog open={resumePreview} onOpenChange={setResumePreview}>
        <DialogContent className="h-[80vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resume Preview</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded-lg border border-border/40">
            {resumeFile && (
              <iframe
                src={URL.createObjectURL(resumeFile)}
                className="h-full min-h-[60vh] w-full"
                title="Resume Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
